<template>
  <div class="eligibility-filter">
    <slot name="activator"></slot>
    <v-card v-if="!empty">
      <v-card-text>
        <span> Applied Filters </span>
        <v-chip
          label
          small
          close
          v-for="key in appliedFilters"
          :key="key"
          @input="clearFilter(key)"
        >
          {{ toSentenceCase(key) }}
        </v-chip>
      </v-card-text>
    </v-card>
    <v-navigation-drawer
      fixed
      app
      right
      hide-overlay
      overflow
      @input="toggleShow"
      :value="show"
      class="drawer"
    >
      <v-card flat>
        <v-card-title class="pb-0">
          <v-spacer />
          <v-icon @click="toggleShow(false)" style="cursor: pointer;">mdi-close</v-icon>
        </v-card-title>

        <v-card-text class="text-xs-center pt-0">
          <v-layout column fill-height>
            <v-flex>
              <v-icon large> mdi-filter </v-icon>
            </v-flex>
            <v-flex>
              <v-card-text class="title"> Eligibilities Filter </v-card-text>
            </v-flex>
            <v-btn block outline @click="reset" :disabled="empty">
              <v-icon left>mdi-filter-remove</v-icon>Clear Filters
            </v-btn>
            <v-flex>
              <v-btn
                @click="
                  () => {
                    filters.complete = false
                    filters.referred = false
                    submitSearch()
                  }
                "
                :flat="filters.complete || filters.referred"
                >All applications</v-btn
              >
              <v-btn
                @click="
                  () => {
                    filters.complete = true
                    filters.referred = false
                    submitSearch()
                  }
                "
                :flat="filters.complete !== true"
              >
                Only completed</v-btn
              >
              <v-btn
                @click="
                  () => {
                    filters.referred = true
                    filters.complete = false
                    submitSearch()
                  }
                "
                :flat="filters.referred !== true"
              >
                Only referred</v-btn
              >
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-text>
          <v-form>
            <v-text-field
              clearable
              solo-inverted
              label="Search by Name"
              v-model="filters.name"
              @keypress.enter="applyFilter"
              @input="submitSearch"
              hint="e.g. Linguina Nettlewater"
            />

            <v-autocomplete
              solo-inverted
              multiple
              dense
              clearable
              label="Filter by Institution"
              :items="institutions"
              v-model="filters.institutions"
              :menu-props="{ maxHeight: '30%' }"
              item-text="name"
              item-value="id"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-bank</v-icon>
                  <div>
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-autocomplete>

            <v-autocomplete
              solo-inverted
              multiple
              clearable
              dense
              label="Filter by Category"
              :items="qualificationCategories"
              v-model="filters.categories"
              :menu-props="{ maxHeight: '30%' }"
              item-text="name"
              item-value="id"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-school</v-icon>
                  <div>
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-autocomplete>

            <v-autocomplete
              solo-inverted
              multiple
              clearable
              dense
              label="Filter by Type"
              :items="qualificationTypes"
              :menu-props="{ maxHeight: '30%' }"
              v-model="filters.types"
              item-text="name"
              item-value="id"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-certificate</v-icon>
                  <div>
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-autocomplete>

            <v-autocomplete
              multiple
              clearable
              dense
              solo-inverted
              label="Filter by Discipline"
              :items="disciplines"
              v-model="filters.disciplines"
              item-text="name"
              item-value="id"
              :menu-props="{ maxHeight: '30%' }"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-lightbulb</v-icon>
                  <div>
                    {{ item.name }}
                  </div>
                </div>
              </template>
            </v-autocomplete>

            <v-label>Filter by academic results</v-label>
            <v-range-slider
              v-if="!filters.grade || filters.grade.length == 0"
              :value="[0, 100]"
              @input="setGrade"
              thumb-label
              :thumb-size="24"
              ticks="always"
              step="5"
              key="passive-grade-filter"
            />
            <v-range-slider
              v-else
              v-model="filters.grade"
              @input="submitSearch"
              thumb-label
              :thumb-size="24"
              ticks="always"
              step="5"
              key="active-grade-filter"
            />

            <v-autocomplete
              solo-inverted
              multiple
              clearable
              dense
              label="Filter by Ethnicity"
              :items="ethnicities"
              v-model="filters.ethnicities"
              :menu-props="{ maxHeight: '30%' }"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-account-group</v-icon>
                  <div>
                    {{ item }}
                  </div>
                </div>
              </template>
            </v-autocomplete>

            <v-autocomplete
              solo-inverted
              multiple
              clearable
              dense
              label="Filter by Gender"
              :items="['Male', 'Female', 'Other']"
              v-model="filters.genders"
              :menu-props="{ maxHeight: '30%' }"
              append-icon="mdi-chevron-down"
              @input="submitSearch"
            >
              <template slot="selection" slot-scope="{ item }">
                <div class="caption selection-chip">
                  <v-icon small left>mdi-human-male-female</v-icon>
                  <div>
                    {{ item }}
                  </div>
                </div>
              </template>
            </v-autocomplete>
          </v-form>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>

<script>
// Data
import ethnicities from '@/data/ethnicities'

// Helpers
import { mapGetters, mapState } from 'vuex'
import _ from 'lodash'

// Mixins
import { caseConverters } from '@/mixins/caseConverters.js'
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

export default {
  mixins: [caseConverters, managedQueryParameters],

  model: {
    prop: 'show',
    event: 'input',
  },

  props: { show: Boolean },

  created() {
    this.$store.dispatch('institutions/fetchAll')
    this.$store.dispatch('qualificationCategories/fetchAll')
    this.$store.dispatch('qualificationTypes/fetchAll')
    this.$store.dispatch('disciplines/fetchAll')
    this.initializeFilters()
    if (!this.empty) {
      this.$emit('input', !this.show)
      this.applyFilter()
    }
  },

  data() {
    return {
      filters: {
        complete: false,
        referred: false,
        categories: [],
        disciplines: [],
        ethnicities: [],
        genders: [],
        grade: [],
        institutions: [],
        name: '',
        types: [],
      },
      ethnicities,
    }
  },

  computed: {
    ...mapGetters({
      institutions: 'institutions/all',
      qualificationCategories: 'qualificationCategories/all',
      qualificationTypes: 'qualificationTypes/all',
      disciplines: 'disciplines/all',
      storeFilters: 'admin/eligibilities/filters',
    }),

    appliedFilters() {
      return [
        'name',
        'institutions',
        'categories',
        'types',
        'disciplines',
        'grade',
        'ethnicities',
        'genders',
      ].filter(
        key =>
          this.storeFilters[key] &&
          (this.storeFilters[key].length > 0 || this.storeFilters[key] === true)
      )
    },

    empty() {
      return !Object.keys(this.filters).some(f => {
        return (
          this.filters[f] &&
          (this.filters[f].length > 0 ||
            typeof this.filters[f] === 'number' ||
            this.filters[f] === true)
        )
      })
    },
  },

  methods: {
    toggleShow(e) {
      this.$emit('input', e)
    },

    reset() {
      Object.keys(this.filters).map(f => {
        if (this.filters[f] === true) {
          this.filters[f] = false
        } else if (Number(this.filters[f])) {
          // All the numbers are strings
          this.filters[f] = ''
        } else if (this.filters[f].length > 0) {
          this.filters[f] = []
        }
      })
      this.applyFilter()
    },

    submitSearch: _.debounce(function() {
      this.applyFilter()
    }, 400),

    applyFilter() {
      this.updateQueryParameters({ ...this.filters })
      this.$store.dispatch('admin/eligibilities/updateFilters', this.filters)
    },

    clearFilter(filter) {
      if (this.filters[filter] == true) this.filters[filter] = false
      if (Number(this.filters[filter])) {
        this.filters[filter] = ''
      }
      if (this.filters[filter].length > 0) this.filters[filter] = []
      this.updateQueryParameters({ ...this.filters })
      this.$store.dispatch('admin/eligibilities/updateFilters', this.filters)
    },

    initializeFilters() {
      Object.keys(this.filters).forEach(f => {
        // No value
        if (!this.initialQueryParameters[f]) {
          return
        }

        // Filter type is array and query param is array
        if (
          typeof this.filters[f] === 'object' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = this.initialQueryParameters[f].map(param =>
            Number(param) ? Number(param) : param
          )
          return
        }

        // Filter type is array and query param is string
        if (
          typeof this.filters[f] === 'object' &&
          typeof this.initialQueryParameters[f] === 'string'
        ) {
          this.filters[f].push(
            Number(this.initialQueryParameters[f])
              ? Number(this.initialQueryParameters[f])
              : this.initialQueryParameters[f]
          )
          return
        }

        // Filter type is string and query param is array
        if (
          typeof this.filters[f] === 'string' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = Number(this.initialQueryParameters[f][0])
            ? Number(this.initialQueryParameters[f][0])
            : this.initialQueryParameters[f][0]
          return
        }

        // Filter type is boolean and query param is array
        if (
          typeof this.filters[f] === 'boolean' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = !!this.initialQueryParameters[f][0]
          return
        }

        // Other
        this.filters[f] = this.initialQueryParameters[f]
      })
    },

    setGrade() {
      this.filters.grade = [0, 100]
      this.submitSearch()
    },
  },
}
</script>

<style>
.eligibility-filter .selection-chip {
  display: table;
  margin: 0.2rem 0;
  vertical-align: middle;
}
.eligibility-filter .selection-chip * {
  display: table-cell;
  vertical-align: middle;
  padding: 0.3rem 0.5rem;
}

.eligibility-filter .v-input__slot i {
  cursor: pointer;
}

.eligibility-filter .drawer {
  padding-bottom: 100px;
}
</style>
